import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { VbButton, VbEmpty, VbError, VbLoading, VbPageHeader } from "@/components/vb";
import { useWorkspace } from "@/context/WorkspaceContext";
import { readLandingParams } from "@/hooks/useLandingParams";
import { companyDestination, pickLandingCompany, readLastCompanyId } from "@/lib/landing";

/**
 * `/` — the landing resolver (AITS-540).
 *
 * There is no Home screen any more. Everything that used to send people "home"
 * (login, signup, onboarding, post-upgrade returns, the report dialogs) still
 * navigates to `/`; this resolves that to the company the user actually works
 * in and redirects. It renders a page only when there's genuinely nothing to
 * open — a new or guest account with no company in the active workspace.
 */

/**
 * Redirect circuit-breaker — a backstop, not a routine path.
 *
 * The known ping-pong sources are gone — Projects.tsx now shows a retryable
 * error instead of bouncing here on a failed load, and a company with no report
 * keeps its own dashboard rather than redirecting anywhere — so nothing should
 * trip this today. It stays because `/` redirects based on data that can disagree with what the target
 * route then fetches, and the failure mode without it is an infinite loop.
 *
 * Tuned to catch machine-speed loops only: a real one cycles in a few hundred
 * milliseconds, so five arrivals inside two seconds is not a person. The looser
 * 3-in-5s it replaced could be tripped by someone impatiently clicking the
 * logo. Module-level, so it resets on reload — the right lifetime, since a
 * reload is the user asking us to try again.
 */
const REDIRECT_WINDOW_MS = 2000;
const REDIRECT_LIMIT = 5;
let recentRedirects: { path: string; at: number }[] = [];

const wouldLoop = (path: string): boolean => {
  const now = Date.now();
  recentRedirects = recentRedirects.filter((r) => now - r.at < REDIRECT_WINDOW_MS);
  return recentRedirects.filter((r) => r.path === path).length >= REDIRECT_LIMIT;
};

type Decision = "pending" | "go" | "empty" | "blocked";

const LandingRedirect: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeWorkspace, loading } = useWorkspace();
  const [selectedPlan] = useState(() => readLandingParams().selected_plan ?? null);
  const [decision, setDecision] = useState<Decision>("pending");
  const decidedFor = useRef<string | null | undefined>(undefined);

  const destination = useMemo(() => {
    if (loading || !activeWorkspace) return null;
    const company = pickLandingCompany(
      activeWorkspace,
      readLastCompanyId(activeWorkspace.id),
    );
    return company ? companyDestination(company) : null;
  }, [loading, activeWorkspace]);

  // Decide in an effect, not during render: <Navigate> redirects from an effect
  // of its own, and child effects run before the parent's — so rendering it
  // before the breaker has counted this arrival would let the loop through.
  //
  // Re-decides whenever `destination` changes rather than latching on the first
  // answer. Creating a first company happens *on this screen*, and the workspace
  // refresh that follows changes `destination` from null to a real company — a
  // one-shot decision would strand the user on "add your first company" right
  // after they added one.
  useEffect(() => {
    if (loading || selectedPlan) return;
    if (decision !== "pending" && decidedFor.current === destination) return;
    decidedFor.current = destination;

    if (destination == null) {
      setDecision("empty");
      return;
    }
    if (wouldLoop(destination)) {
      setDecision("blocked");
      return;
    }
    recentRedirects.push({ path: destination, at: Date.now() });
    setDecision("go");
  }, [decision, loading, destination, selectedPlan]);

  if (selectedPlan) {
    return <Navigate to="/settings?tab=billing" replace state={location.state} />;
  }

  if (decision === "pending") {
    return (
      <div className="vb-page">
        <VbLoading rows={2} />
      </div>
    );
  }

  if (decision === "go" && destination) {
    // Forward nav state: the shell reads `openGenerateDialog` /
    // `openReportDialog` off it, and callers set that while navigating here.
    return <Navigate to={destination} replace state={location.state} />;
  }

  if (decision === "blocked") {
    return (
      <div className="vb-page space-y-6">
        <VbError
          title="We couldn't open your company"
          description="Something went wrong loading it, so we stopped rather than keep retrying. Pick a company from the sidebar, or try again."
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className="vb-page space-y-6">
      <VbPageHeader
        title="Add your first company"
        subtitle="Run an AI Trust Signals report to see how AI describes your business."
      />
      <VbEmpty
        title="Ready to see how your company looks to AI?"
        description="We'll check your site against the AI Trust Signals that language models rely on, then show you what they say and what to fix first."
        action={
          // The shell owns ReportDialog (and prefills it from the marketing-site
          // params), so ask it to open rather than mounting a second copy.
          <VbButton
            onClick={() =>
              navigate(location.pathname, {
                replace: true,
                state: { openReportDialog: true },
              })
            }
          >
            Check my AI Trust Signals score
          </VbButton>
        }
      />
    </div>
  );
};

export default LandingRedirect;
